import { useMemo } from 'react';
import type { AnswerSlot } from '../../types';

interface DragDropSlotsProps {
  slots: AnswerSlot[];
  selections: Record<number, string>;
  onSelect: (slotId: number, value: string) => void;
  revealed: boolean;
  hintRevealed?: boolean;
}

export default function DragDropSlots({
  slots,
  selections,
  onSelect,
  revealed,
  hintRevealed,
}: DragDropSlotsProps) {
  // Build a set of choices already selected in other slots
  const selectedElsewhere = useMemo(() => {
    const map = new Map<number, Set<string>>();
    for (const slot of slots) {
      const others = new Set<string>();
      for (const s of slots) {
        if (s.slot_id !== slot.slot_id) {
          const val = selections[s.slot_id];
          if (val) others.add(val);
        }
      }
      map.set(slot.slot_id, others);
    }
    return map;
  }, [slots, selections]);

  return (
    <div className="space-y-4">
      {slots.map((slot) => {
        const selected = selections[slot.slot_id] ?? '';
        const isCorrect = selected === slot.correct_answer;
        const hasValue = selected !== '';
        const othersUsed = selectedElsewhere.get(slot.slot_id) ?? new Set();

        return (
          <div key={slot.slot_id} className="space-y-2">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Step {slot.slot_id}
            </p>

            <select
              value={selected}
              onChange={(e) => onSelect(slot.slot_id, e.target.value)}
              disabled={revealed}
              aria-label={`Step ${slot.slot_id} selection`}
              className={`w-full rounded-xl border-2 px-4 py-2.5 text-sm font-medium transition-all
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40
                dark:bg-card-dark
                ${revealed
                  ? hasValue && isCorrect
                    ? 'border-success bg-green-50 text-green-800 dark:border-success dark:bg-green-900/20 dark:text-green-300'
                    : hasValue && !isCorrect
                      ? 'border-error bg-red-50 text-red-800 dark:border-error dark:bg-red-900/20 dark:text-red-300'
                      : 'border-slate-200 bg-slate-50 text-slate-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-600'
                  : hasValue
                    ? 'border-primary bg-primary/5 dark:border-primary dark:bg-primary/10'
                    : 'border-slate-200 dark:border-white/10'
                }`}
            >
              <option value="">-- Select --</option>
              {slot.choices.map((choice) => {
                const usedByOther = othersUsed.has(choice);
                return (
                  <option
                    key={choice}
                    value={choice}
                    className={usedByOther ? 'text-slate-400' : ''}
                  >
                    {choice}{usedByOther ? ' (selected)' : ''}
                  </option>
                );
              })}
            </select>

            {/* Reveal: show correct answer below incorrect selections */}
            {revealed && hasValue && !isCorrect && (
              <p className="text-xs font-medium text-success">
                Correct: {slot.correct_answer}
              </p>
            )}

            {/* Hint: show correct answer as a green badge */}
            {hintRevealed && !revealed && (
              <span className="inline-block rounded-full bg-green-100 px-3 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/30 dark:text-green-300">
                Answer: {slot.correct_answer}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
